import classNames from "classnames";
import { Drawer } from "@breward/ui";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from "@breward/i18n";

const languageMap: Record<string, string> = {
    'en': 'en-GB',
    'pl': 'pl-PL'
};

interface MenuItem {
  label?: string;
  key: string;
  icon: string;
  badge?: boolean;
}

interface MobileMenuProps {
  items: MenuItem[];
  onLogout: () => void;
  opened: boolean;
  onClose: (opened: boolean) => void;
  basePath?: string;
}

export const MobileMenu = ({ items, onLogout, opened, onClose, basePath = "/app" }: MobileMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const appBasePath = params?.id ? `${basePath}/${params.id}/` : `${basePath}/`;
  const normalizedPath = location.pathname.replace(/\/+$/, "");
  const currentItemKey = normalizedPath.startsWith(appBasePath)
    ? normalizedPath.slice(appBasePath.length)
    : normalizedPath.replace(new RegExp(`^${basePath}/?`), "");
  const currentRoot = currentItemKey.split("/")[0];

  const changeLanguageWithRegion = (langCode: string) => {
    const fullLanguageCode = languageMap[langCode] || langCode;
    i18n.changeLanguage(fullLanguageCode);
    localStorage.setItem('i18nextLng', fullLanguageCode);
  };

  const handleLanguageChange = (language: string) => {
    changeLanguageWithRegion(language);
  };

  const handleNavigation = (key: string) => {
    navigate(key);
    onClose(false);
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => onClose(false)}
      width="clamp(16.5rem, 84vw, 21rem)"
      className="border-l border-primary-200/60 bg-white/70 backdrop-blur-2xl dark:border-primary-800/60 dark:bg-content-900/70"
    >
      <div className="flex h-full flex-col gap-2">
        {items?.map((menuItem: MenuItem) => {
          const itemRoot = menuItem.key.split("/")[0];
          const isActive = currentRoot === itemRoot;
          const menuLabel =
            menuItem.label ||
            itemRoot.charAt(0).toUpperCase() + itemRoot.slice(1).replace(/-/g, " ");

          return (
            <button
              type="button"
              onClick={() => handleNavigation(menuItem.key)}
              
              key={menuItem.key}
              className={classNames(
                "flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-all",
                isActive
                  ? "border-primary-300/70 bg-white/70 shadow-sm shadow-primary-500/15 dark:border-primary-500/45 dark:bg-primary-900/35"
                  : "border-transparent bg-white/35 hover:border-primary-200/60 hover:bg-white/55 dark:bg-content-900/20 dark:hover:bg-content-800/45"
              )}
            >
              <div
                className={classNames(
                  "h-6 w-6 transition-all",
                  isActive ? "text-primary-600 dark:text-primary-300" : "text-content-600 dark:text-content-400",
                  menuItem.icon
                )}
              />
              <div className="flex-1">
                <div
                  className={classNames(
                    "font-sans text-sm font-medium",
                    isActive ? "text-primary-800 dark:text-primary-100" : "text-content-700 dark:text-content-200"
                  )}
                >
                  {menuLabel}
                </div>
              </div>
              {menuItem.badge && (
                <div className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
              )}
            </button>
          );
        })}

        <div className="flex w-full items-center gap-3 rounded-2xl border border-transparent bg-white/35 px-3 py-3 text-left dark:bg-content-900/20 mt-auto mb-2">
          <div className="icon-[ph--translate] h-6 w-6 text-content-600 dark:text-content-400" />
          <div className="relative flex-1">
            <select
              className="appearance-none w-full bg-transparent text-content-700 dark:text-content-200 font-sans text-sm font-medium focus:outline-none focus:ring-0"
              onChange={(e) => handleLanguageChange(e.target.value)}
              value={i18n.language.substring(0, 2)}
              aria-label={i18n.t('global:shared.language', 'Language')}
            >
              <option value="en">English (EN)</option>
              <option value="pl">Polski (PL)</option>
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <div className="icon-[ph--caret-down] text-content-400 h-3.5 w-3.5" />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            onLogout();
            onClose(false);
          }}
          
          key={"logout"}
          className="flex w-full items-center gap-3 rounded-2xl border border-error-200/70 bg-white/45 px-3 py-3 text-left transition-all hover:bg-error-50/70 dark:border-error-800/60 dark:bg-error-900/15 dark:hover:bg-error-900/30"
        >
          <div
            className={classNames(
              "icon-[ph--sign-out] h-6 w-6 text-error-600 dark:text-error-400"
            )}
          />
          <div
            className={classNames("font-sans text-sm font-medium text-error-700 dark:text-error-300")}
          >
            {i18n.t("global:shared.logout", "Logout")}
          </div>
        </button>
      </div>
    </Drawer>
  );
};
